import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Form from 'components/form';
import Input from 'components/form/TextInput';
import Select from 'components/form/Select';
import Button from 'components/Button';
import Spin from 'components/Spin';
import countries from 'constants/countries';
import toast from 'utils/toast';
import { getTwillioNumbers, provisionNumber } from 'services/account.service';
import { loadAccount } from 'store/modules/settings/actions';
import { getMe } from 'store/modules/user/actions';

const AddConnectionForm = ({ onConnectionSaved }) => {
  const [t] = useTranslation('onboarding');
  const dispatch = useDispatch();
  const account = useSelector((state) => state.settings.account);
  const [state, setState] = useState({
    numbers: [],
    loadingNumbers: false,
    number: '',
    country: account.account_country,
    areaCode: '',
    noNumbers: false,
  });

  useEffect(() => {
    if (state.country && state.areaCode.length === 3) {
      fetchNumbers();
    }
  }, [state.country, state.areaCode]);

  const onCountrySelect = (val) => {
    setState({
      ...state,
      country: val,
      areaCode: '',
      noNumbers: false,
    });
  };

  const onAreaChange = (e) => {
    const { value } = e.target;
    setState({
      ...state,
      areaCode: value,
    });
  };

  const refreshList = (e) => {
    e.preventDefault();
    fetchNumbers();
  };

  const fetchNumbers = () => {
    setState({
      ...state,
      loadingNumbers: true,
      noNumbers: false,
      numbers: [],
    });
    getTwillioNumbers(state.country.toUpperCase(), state.areaCode).then(
      (numbers) => {
        if (!numbers.length) {
          setState({
            ...state,
            noNumbers: true,
            loadingNumbers: false,
            numbers: [],
          });
        } else {
          setState({
            ...state,
            noNumbers: false,
            loadingNumbers: false,
            numbers: numbers.map((i) => ({ label: i, value: i })),
          });
        }
      },
    );
  };

  const onNumberSelect = (val) => {
    setState({
      ...state,
      number: val,
    });
  };

  const onSave = () => {
    const { number, areaCode, country } = state;
    const countryCode = country.toUpperCase();
    // @ts-ignore;
    return provisionNumber(countryCode, number).then(
      (resp) => {
        onConnectionSaved && onConnectionSaved(resp);
        dispatch(loadAccount(account.account_id));
        dispatch(getMe());
      },
      (e) => toast.error(e.message),
    );
  };

  return (
    <div>
      <Box textAlign="center" mb={3}>
        <Typography variant="h1">
          {t('select-number-heading', 'Select Your Phone Number')}
        </Typography>
        <Typography>
          {t(
            'select-number-desc',
            'You choose the number that sounds best to you, and we do the work in the background to get everything setup!',
          )}
        </Typography>
      </Box>
      <Spin spinning={state.loadingNumbers}>
        <Form>
          <Box>
            <Select
              fullWidth
              transformValue
              label={t('select-country-label', 'Select Your Country')}
              options={countries}
              value={state.country}
              onChange={onCountrySelect}
            />
          </Box>
          <Box>
            <Input
              fullWidth
              label={t('select-zip-label', 'Enter Your Area/Dialing Code')}
              value={state.areaCode}
              onChange={onAreaChange}
            />
          </Box>
          {state.numbers.length ? (
            <Box>
              <Select
                transformValue
                fullWidth
                isLoading={state.loadingNumbers}
                label={t('select-number-label', 'Select a Phone Number')}
                hint={t(
                  'select-number-hint',
                  "This number is a new number to use to communicate via text with your congregation. It's not a spammy shortcode, but rather a personal 10 digit local number.",
                )}
                options={state.numbers}
                value={state.number}
                onChange={onNumberSelect}
              />
              <Button variant="link" onClick={refreshList}>
                <small>{t('refresh-list', 'Refresh List')}</small>
              </Button>
            </Box>
          ) : null}
          {state.noNumbers && (
            <Alert severity="warning">
              {t(
                'no-numbers',
                'There are currently no phone numbers available for this area/dialing code. Please try a different number.',
              )}
            </Alert>
          )}
        </Form>
      </Spin>
      <Box my={2} textAlign="center">
        <Button disabled={!state.number} onClick={onSave}>
          {t('save-number', 'Save Phone Number')}
        </Button>
      </Box>
    </div>
  );
};

export default AddConnectionForm;
